import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { approvalService } from 'api/services/approvalService';
import type { ContributedApproval } from 'api/types/approval.types';
import notificationService from 'services/notificationService';
import { formatCurrency } from 'components/modals/modules/booking-module/booking.utils';

/**
 * Other modules' approvals in the inbox (S3b): today the restaurant's requisitions held by an approval
 * rule. They are not approval requests of the booking kind, so they sit in their own band above the
 * grid instead of in it, and are decided by the module that owns them (the server checks the caller
 * holds the role the item is routed to). Renders nothing when there is nothing to decide.
 */
interface Props {
  /** Bumped by the inbox's Refresh button. */
  refreshKey: number;
}

const ago = (iso: string): string => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  return hr < 24 ? `${hr}h ago` : `${Math.round(hr / 24)}d ago`;
};

const ContributedApprovalsPanel: React.FC<Props> = ({ refreshKey }) => {
  const [items, setItems] = useState<ContributedApproval[]>([]);
  const [open, setOpen] = useState(true);
  const [deciding, setDeciding] = useState<{ item: ContributedApproval; kind: 'approve' | 'reject' } | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await approvalService.getContributed();
      setItems(res.data ?? []);
    } catch {
      // The band is extra: a failure here must not break the booking inbox below it.
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const decide = useCallback(async () => {
    if (!deciding) return;
    const rejecting = deciding.kind === 'reject';
    if (rejecting && note.trim().length === 0) {
      notificationService.toast.warning('Give the reason for rejecting — the requester sees it.');
      return;
    }
    setSaving(true);
    try {
      await approvalService.decideContributed(deciding.item, deciding.kind, note.trim() || undefined);
      notificationService.toast.success(rejecting ? `${deciding.item.number} rejected.` : `${deciding.item.number} approved.`);
      setDeciding(null);
      load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The decision could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [deciding, note, load]);

  if (items.length === 0) return null;

  return (
    <div style={{ margin: '6px 1.25rem 0', border: '1px solid var(--border-color)', borderRadius: '8px', background: 'var(--bg-primary)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem',
          border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)',
        }}
      >
        {open ? <IconSystem.chevronDown size={14} /> : <IconSystem.chevronRight size={14} />}
        Restaurant and other modules
        <span className="badge badge-pending" style={{ marginLeft: '0.25rem' }}>{items.length}</span>
      </button>
      {open && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
              <th style={{ padding: '0.3rem 0.75rem', fontWeight: 600 }}>Type</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>Number</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>For</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>Items</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600, textAlign: 'right' }}>Value</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>Where</th>
              <th style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>Requested</th>
              <th style={{ padding: '0.3rem 0.75rem' }} />
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={`${i.source}-${i.itemId}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.35rem 0.75rem' }}>{i.typeLabel}</td>
                <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: 'var(--primary)' }}>{i.number}</td>
                <td style={{ padding: '0.35rem 0.5rem' }}>{i.title}</td>
                <td style={{ padding: '0.35rem 0.5rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.detail ?? ''}>
                  {i.detail || '—'}
                </td>
                <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{i.amount != null ? `₹${formatCurrency(i.amount)}` : '—'}</td>
                <td style={{ padding: '0.35rem 0.5rem' }}>{i.origin || '—'}</td>
                <td style={{ padding: '0.35rem 0.5rem' }} title={new Date(i.requestedAt).toLocaleString()}>
                  {i.requestedByName || '—'} · {ago(i.requestedAt)}
                </td>
                <td style={{ padding: '0.35rem 0.75rem', whiteSpace: 'nowrap', textAlign: 'right' }}>
                  <button className="btn btn-primary btn-sm" onClick={() => { setNote(''); setDeciding({ item: i, kind: 'approve' }); }}>Approve</button>{' '}
                  <button className="btn btn-danger btn-sm" onClick={() => { setNote(''); setDeciding({ item: i, kind: 'reject' }); }}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deciding && (
        <>
          <div className="modal-backdrop" onClick={() => !saving && setDeciding(null)} />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{deciding.kind === 'approve' ? 'Approve' : 'Reject'} {deciding.item.number}</h2>
              <button className="modal-close" onClick={() => setDeciding(null)} disabled={saving}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                {deciding.item.typeLabel} for <strong>{deciding.item.title}</strong>
                {deciding.item.amount != null && <> · ₹{formatCurrency(deciding.item.amount)}</>}
                {deciding.item.origin && <> · {deciding.item.origin}</>}
              </p>
              {deciding.item.detail && <p style={{ fontSize: '0.8rem', marginTop: 0 }}>{deciding.item.detail}</p>}
              <div className="form-group">
                <label className="form-label">
                  {deciding.kind === 'reject' ? <>Reason <span className="text-danger">*</span></> : 'Note (optional)'}
                </label>
                <textarea
                  className="form-input form-textarea"
                  rows={3}
                  maxLength={300}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={saving}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setDeciding(null)} disabled={saving}>Cancel</button>
              <button
                className={`btn ${deciding.kind === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                onClick={decide}
                disabled={saving || (deciding.kind === 'reject' && note.trim().length === 0)}
              >
                {saving ? 'Saving...' : deciding.kind === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContributedApprovalsPanel;
